import { Injectable } from '@angular/core';
import mapboxgl from 'mapbox-gl';
import { IZona } from 'modelosSeguridad/src';

/**
 * ZonasSeguridadLayerManager
 *
 * Renderiza zonas de seguridad (polígonos) en el mapa.
 * Patrón análogo a PolygonsLayerManager pero con source/layers propios para
 * permitir toggles independientes en el flotante.
 */
@Injectable({
  providedIn: 'root',
})
export class ZonasSeguridadLayerManager {
  private map: mapboxgl.Map | null = null;
  private sourceId = 'zonas-seguridad-source';
  private fillLayerId = 'zonas-seguridad-fill-layer';
  private outlineLayerId = 'zonas-seguridad-outline-layer';
  private isInitialized = false;

  private readonly defaultFill = '#3F51B5';
  private readonly defaultOutline = '#1A237E';

  private pendingFeatures: GeoJSON.Feature<GeoJSON.Polygon>[] | null = null;

  initialize(map: mapboxgl.Map): void {
    if (this.isInitialized) return;
    this.map = map;
    this.createSourceAndLayers();
    this.isInitialized = true;
  }

  cleanup(): void {
    if (!this.map) return;
    try {
      if (this.map.getLayer(this.outlineLayerId)) {
        this.map.removeLayer(this.outlineLayerId);
      }
      if (this.map.getLayer(this.fillLayerId)) {
        this.map.removeLayer(this.fillLayerId);
      }
      if (this.map.getSource(this.sourceId)) {
        this.map.removeSource(this.sourceId);
      }
    } catch {
      // map.style ya disposed
    }
    this.map = null;
    this.isInitialized = false;
  }

  private createSourceAndLayers(): void {
    if (!this.map) return;

    if (!this.map.getSource(this.sourceId)) {
      this.map.addSource(this.sourceId, {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
        buffer: 128,
        tolerance: 0.375,
      });
    }

    if (!this.map.getLayer(this.fillLayerId)) {
      this.map.addLayer({
        id: this.fillLayerId,
        type: 'fill',
        source: this.sourceId,
        paint: {
          'fill-color': ['coalesce', ['get', 'fillColor'], this.defaultFill],
          'fill-opacity': 0.32,
        },
      });
    }

    if (!this.map.getLayer(this.outlineLayerId)) {
      this.map.addLayer({
        id: this.outlineLayerId,
        type: 'line',
        source: this.sourceId,
        layout: { 'line-join': 'round', 'line-cap': 'round' },
        paint: {
          'line-color': ['coalesce', ['get', 'color'], this.defaultOutline],
          'line-width': 2.5,
          'line-opacity': 1,
        },
      });
    }

    this.moveBelowEntityLayers();
  }

  /**
   * Mueve fill+outline al fondo del stack, justo encima del basemap pero
   * debajo de cualquier layer de entity-manager (pins, sirenas, alertas, etc).
   */
  private moveBelowEntityLayers(): void {
    if (!this.map) return;
    try {
      const allLayers = this.map.getStyle()?.layers || [];
      const entityPrefixes = [
        'alertasSeguridad',
        'eventosSeguridad',
        'sirenas',
        'alarmas',
        'vehiculos',
        'colectivos',
        'trackers',
        'activos',
        'luminarias',
        'gateways',
        'paradas',
        'puntos',
        'emergencias',
        'hospitales',
        'ambulancias',
        'camiones',
        'centros',
      ];
      const anchor = allLayers.find((l) =>
        entityPrefixes.some((p) => l.id.startsWith(p)),
      );
      if (anchor) {
        this.map.moveLayer(this.fillLayerId, anchor.id);
        this.map.moveLayer(this.outlineLayerId, anchor.id);
      }
    } catch {
      // ignore
    }
  }

  updateZonas(zonas: IZona[]): void {
    if (!this.map || !this.isInitialized) return;

    const features: GeoJSON.Feature<GeoJSON.Polygon>[] = [];

    for (const zona of zonas) {
      // Preferir geojson.coordinates; fallback a coordenadas[]
      let coords: [number, number][][] | undefined;
      if (
        zona.geojson?.coordinates &&
        Array.isArray(zona.geojson.coordinates) &&
        zona.geojson.coordinates.length > 0
      ) {
        coords = zona.geojson.coordinates as [number, number][][];
      } else if (
        Array.isArray(zona.coordenadas) &&
        zona.coordenadas.length >= 3
      ) {
        const ring = zona.coordenadas
          .filter(
            (c: any) => typeof c?.lng === 'number' && typeof c?.lat === 'number',
          )
          .map((c: any) => [c.lng, c.lat] as [number, number]);
        if (ring.length >= 3) {
          // Cerrar anillo si no está cerrado
          const first = ring[0];
          const last = ring[ring.length - 1];
          if (first[0] !== last[0] || first[1] !== last[1]) {
            ring.push([first[0], first[1]]);
          }
          coords = [ring];
        }
      }

      if (!coords) continue;

      features.push({
        type: 'Feature',
        id: zona._id || `zona-${features.length}`,
        geometry: {
          type: 'Polygon',
          coordinates: coords as any,
        },
        properties: {
          id: zona._id,
          nombre: zona.nombre,
          fillColor: this.defaultFill,
          color: this.defaultOutline,
        },
      });
    }

    this.applyFeatures(features);
  }

  private applyFeatures(features: GeoJSON.Feature<GeoJSON.Polygon>[]): void {
    if (!this.map) return;
    const source = (() => {
      try {
        return this.map.getSource(this.sourceId) as mapboxgl.GeoJSONSource;
      } catch {
        return null;
      }
    })();

    if (source) {
      source.setData({ type: 'FeatureCollection', features });
      this.pendingFeatures = null;
    } else {
      // Source aún no listo — buffer y reintentar cuando el estilo esté idle
      this.pendingFeatures = features;
      this.map.once('idle', () => {
        if (this.pendingFeatures) {
          this.applyFeatures(this.pendingFeatures);
        }
      });
    }
  }

  clear(): void {
    if (!this.map || !this.isInitialized) return;
    if (!this.map.isStyleLoaded()) return;
    try {
      const source = this.map.getSource(
        this.sourceId,
      ) as mapboxgl.GeoJSONSource;
      source?.setData({ type: 'FeatureCollection', features: [] });
    } catch {
      // ignore
    }
  }

  setVisibility(visible: boolean): void {
    if (!this.map) return;
    const visibility = visible ? 'visible' : 'none';
    try {
      if (this.map.getLayer(this.fillLayerId)) {
        this.map.setLayoutProperty(this.fillLayerId, 'visibility', visibility);
      }
      if (this.map.getLayer(this.outlineLayerId)) {
        this.map.setLayoutProperty(
          this.outlineLayerId,
          'visibility',
          visibility,
        );
      }
    } catch {
      // ignore
    }
  }
}
